import React, { useState, useCallback, useRef } from "react";
import { message, Modal } from "antd";
import GenericTable from "./GenericTable";
import { LeadsColumns, LeadsFilters } from "../components/LeadsColumns";
import EditLeadModal from "../components/EditLeadModal";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

export default function LeadsTable({ onOpenWhatsapp }) {
  const { userName, userId } = useAuth();
  const [pickedLeadIds, setPickedLeadIds] = useState(new Set());
  const [loadingLeadIds, setLoadingLeadIds] = useState(new Set());
  const [pickedByMap, setPickedByMap] = useState({});
  const [whatsappLoadingLeadIds, setWhatsappLoadingLeadIds] = useState(new Set());
  const [editingLead, setEditingLead] = useState(null);
  const tableRef = useRef(null);

  const handlePickLead = useCallback(async (leadId) => {
    if (loadingLeadIds.has(leadId)) return;
    setLoadingLeadIds((prev) => new Set([...prev, leadId]));
    try {
      await api.post(`/crm/leads/pick`, { leadId });
      message.success("Lead picked successfully!");
      setPickedLeadIds((prev) => new Set([...prev, leadId]));
      setPickedByMap((prev) => ({ ...prev, [leadId]: userName }));
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to pick lead";
      message.error(errorMsg);
    } finally {
      setLoadingLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  }, [userName, loadingLeadIds]);

  const handleOpenWhatsapp = useCallback(async (lead) => {
    if (whatsappLoadingLeadIds.has(lead.id)) return;

    setWhatsappLoadingLeadIds((prev) => new Set(prev).add(lead.id));
    try {
      // Fetch current chat list fresh so we don't rely on stale state
      const res = await api.get(`/crm/whatsapp/my-chats/${userId}`);
      const chats = res.data?.data ?? [];
      const alreadyChatting = chats.some((c) => {
        console.log("comparing", c.leadId, typeof c.leadId, "vs", lead.id, typeof lead.id);
        return Number(c.leadId) === Number(lead.id);
      });
      if (!alreadyChatting) {
        await api.post("/crm/whatsapp/initiate", {
          userId,
          leadId: lead.id,
        });
      }

      onOpenWhatsapp?.(lead); // tell Dashboard to switch to Chats tab + select this lead
    } catch (err) {
      console.error("Failed to open WhatsApp chat:", err);
      message.error(err.response?.data?.message || "Failed to start WhatsApp chat");
    } finally {
      setWhatsappLoadingLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
    }
  }, [userId, whatsappLoadingLeadIds, onOpenWhatsapp]);

  // Called by the pencil icon in the Action column
  const handleEditLead = useCallback((record) => {
    setEditingLead(record);
  }, []);

  // Called when the modal's Save button succeeds
  const handleSaveLead = useCallback(async (updatedLead) => {
    try {
      const payload = { ...updatedLead, 
        service: updatedLead.serviceRequired,
        phone: updatedLead.phoneNumber,
        country: updatedLead.preferredCountry
       };
      delete payload.serviceRequired;
      delete payload.phoneNumber;
      delete payload.preferredCountry;

      await api.put(`/crm/leads/${updatedLead.id}`, payload);
      message.success("Lead updated");
      setEditingLead(null);
      tableRef.current?.updateRow(updatedLead.id, updatedLead); // patch locally, no refetch
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to update lead");
    }
  }, []);

  const columns = useCallback(
    () =>
      LeadsColumns(
        (id) => handlePickLead(id),
        pickedLeadIds,
        loadingLeadIds,
        pickedByMap,
        handleOpenWhatsapp,
        whatsappLoadingLeadIds,
        userId,
        handleEditLead
      ),
    [handlePickLead, pickedLeadIds, loadingLeadIds, pickedByMap, handleOpenWhatsapp, whatsappLoadingLeadIds, handleEditLead]
  );

  return (
    <>
      <GenericTable
        ref={tableRef}
        apiPath="/crm/leads"
        columns={columns}
        filterOptions={LeadsFilters}
      />
      <Modal
        open={!!editingLead}
        onCancel={() => setEditingLead(null)}
        footer={null}
        closable={false}  
        destroyOnClose
        width={460}
        styles={{ body: { padding: 0 } }}
      >
        {editingLead && (
          <EditLeadModal
            lead={editingLead}
            onCancel={() => setEditingLead(null)}
            onSave={handleSaveLead}
          />
        )}
      </Modal>
    </>
  );
}